import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { Tag, Wrench, ArrowDownToLine, Package, AlertCircle } from 'lucide-react';

export default function AssetScanPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // null | 'service' | 'return'
  const [svc, setSvc] = useState({ type: 'breakdown', cause: 'normal_use', charge_to: 'company', description: '', estimated_cost: '' });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function load() {
    setLoading(true);
    try {
      const r = await apiClient.get(`/assets/scan/${id}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Asset not found');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  async function openService() {
    if (!svc.description.trim()) return toast.error('Description required');
    try {
      await apiClient.post(`/assets/${id}/service`, {
        type: svc.type, cause: svc.cause, charge_to: svc.charge_to,
        description: svc.description,
        estimated_cost: parseFloat(svc.estimated_cost) || 0,
      });
      toast.success(svc.charge_to === 'employee' ? 'Service opened — HR notified' : 'Service ticket opened');
      setMode(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  }

  if (loading) return <div className="flex justify-center items-center h-screen"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return (
    <div className="max-w-md mx-auto mt-10 p-4">
      <Card><CardContent className="py-10 text-center text-muted-foreground">Asset not found or you don't have access.</CardContent></Card>
    </div>
  );

  const a = data.asset;
  const ac = data.actions || {};
  return (
    <div className="max-w-md mx-auto p-4 space-y-4" data-testid="asset-scan-page">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">{a.asset_code}</CardTitle>
            </div>
            <Badge variant="outline">{a.status}</Badge>
          </div>
          <CardDescription>{a.brand} {a.model}</CardDescription>
        </CardHeader>
        <CardContent className="text-sm space-y-1.5">
          <div className="flex justify-between"><span className="text-muted-foreground">UID</span><span>{a.uid || '—'}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Category</span><span>{a.category_name}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Serial</span><span className="text-xs">{a.serial_number || '—'}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Assigned to</span><span>{a.assigned_to_name || '—'}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Current value</span><span className="font-mono text-emerald-700">{a.currency} {(a.current_value || 0).toLocaleString()}</span></div>
        </CardContent>
      </Card>

      {!mode && (
        <div className="space-y-2">
          {data.is_holder && (
            <p className="text-xs text-center text-emerald-700 bg-emerald-500/10 rounded p-2">This asset is assigned to you.</p>
          )}
          {ac.can_request_service && (
            <Button className="w-full" variant="outline" onClick={() => setMode('service')} data-testid="scan-service-btn">
              <Wrench className="h-4 w-4 mr-1.5" /> Request service
            </Button>
          )}
          {ac.can_return && (
            <Button className="w-full" variant="outline" onClick={() => navigate(`/it/asset-management?asset=${id}`)} data-testid="scan-return-btn">
              <ArrowDownToLine className="h-4 w-4 mr-1.5" /> Return / Manage (full page)
            </Button>
          )}
          {data.can_manage && (
            <Button className="w-full" onClick={() => navigate(`/it/asset-management?asset=${id}`)}>
              <Tag className="h-4 w-4 mr-1.5" /> Manage in ERP
            </Button>
          )}
        </div>
      )}

      {mode === 'service' && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Request Service</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={svc.type} onValueChange={v => setSvc({ ...svc, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakdown">Breakdown</SelectItem>
                    <SelectItem value="wear_tear">Wear &amp; tear</SelectItem>
                    <SelectItem value="personal_damage">Personal damage</SelectItem>
                    <SelectItem value="upgrade">Upgrade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Cause</Label>
                <Select value={svc.cause} onValueChange={v => setSvc({ ...svc, cause: v, charge_to: v === 'employee_negligence' ? 'employee' : 'company' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal_use">Normal use</SelectItem>
                    <SelectItem value="accident">Accident</SelectItem>
                    <SelectItem value="employee_negligence">My negligence</SelectItem>
                    <SelectItem value="manufacturer_defect">Manufacturer defect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Charge to</Label>
              <Select value={svc.charge_to} onValueChange={v => setSvc({ ...svc, charge_to: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="employee">Me (personal — HR will be notified)</SelectItem>
                </SelectContent>
              </Select>
              {svc.charge_to === 'employee' && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> HR will be notified to handle this manually.</p>
              )}
            </div>
            <div>
              <Label className="text-xs">Describe the issue *</Label>
              <Textarea rows={3} value={svc.description} onChange={e => setSvc({ ...svc, description: e.target.value })} data-testid="scan-service-desc" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setMode(null)}>Cancel</Button>
              <Button className="flex-1" onClick={openService} data-testid="scan-submit-service">Submit</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
